export const PENDING_REPORTS_STORAGE_KEY = "community-report-pending-reports";

const PENDING_REPORTS_VERSION = 1;

export function classifySubmissionResult(result, payloadCount) {
  if (
    !Number.isInteger(payloadCount) ||
    payloadCount < 1 ||
    !result ||
    typeof result !== "object" ||
    result.error
  ) {
    return malformedOutcome();
  }

  const accepted = validCount(result.accepted);
  const deduped = validCount(result.deduped);
  const rejected = validCount(result.rejected);
  if (accepted === null || deduped === null || rejected === null) {
    return malformedOutcome();
  }

  const completed = accepted + deduped;
  if (completed + rejected !== payloadCount) {
    return malformedOutcome({ accepted, deduped, rejected });
  }

  if (rejected > 0) {
    return {
      kind: completed > 0 ? "partial_rejection" : "rejected",
      complete: false,
      accepted,
      deduped,
      rejected,
    };
  }

  return {
    kind: "completed",
    complete: true,
    status: completionStatus(accepted, deduped),
    accepted,
    deduped,
    rejected,
  };
}

export function queuePendingReports(storage, records, queuedAt = new Date().toISOString()) {
  if (!Array.isArray(records) || records.length === 0) {
    throw new Error("Pending report queue requires at least one report");
  }

  const pending = readPendingReports(storage);
  const byId = new Map(pending.reports.map((item) => [item.record.id, item]));
  records.forEach((record) => {
    const id = reportId(record);
    byId.set(id, {
      queued_at: queuedAt,
      record,
    });
  });

  storage.setItem(PENDING_REPORTS_STORAGE_KEY, JSON.stringify({
    version: PENDING_REPORTS_VERSION,
    reports: [...byId.values()],
  }));
}

export function removePendingReports(storage, records) {
  const ids = new Set((Array.isArray(records) ? records : []).map(reportId));
  if (ids.size === 0) return;

  const pending = readPendingReports(storage);
  const remaining = pending.reports.filter((item) => !ids.has(item.record.id));
  if (remaining.length === 0) {
    storage.removeItem(PENDING_REPORTS_STORAGE_KEY);
    return;
  }

  storage.setItem(PENDING_REPORTS_STORAGE_KEY, JSON.stringify({
    version: PENDING_REPORTS_VERSION,
    reports: remaining,
  }));
}

export function readPendingReports(storage) {
  const serialized = storage.getItem(PENDING_REPORTS_STORAGE_KEY);
  if (!serialized) return { version: PENDING_REPORTS_VERSION, reports: [] };

  let parsed;
  try {
    parsed = JSON.parse(serialized);
  } catch {
    throw new Error("Pending report queue is invalid");
  }

  if (
    parsed?.version !== PENDING_REPORTS_VERSION ||
    !Array.isArray(parsed.reports) ||
    parsed.reports.some((item) => (
      typeof item?.queued_at !== "string" ||
      !item.record ||
      typeof item.record !== "object" ||
      typeof item.record.id !== "string" ||
      !item.record.id
    ))
  ) {
    throw new Error("Pending report queue is invalid");
  }

  return parsed;
}

function validCount(value) {
  return Number.isInteger(value) && value >= 0 ? value : null;
}

function completionStatus(accepted, deduped) {
  if (accepted > 0 && deduped > 0) return "accepted_and_deduped";
  return accepted > 0 ? "accepted" : "deduped";
}

function malformedOutcome(counts = {}) {
  return {
    kind: "malformed",
    complete: false,
    accepted: counts.accepted ?? 0,
    deduped: counts.deduped ?? 0,
    rejected: counts.rejected ?? 0,
  };
}

function reportId(record) {
  const id = record && typeof record === "object" ? record.id : "";
  if (typeof id !== "string" || !id) {
    throw new Error("Pending report requires an ID");
  }
  return id;
}
